import React, { useState, useEffect, useMemo, useRef } from "react";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";

import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import AnimatedButton from "../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import { useTranslation } from "react-i18next";

const StyledScreenshotImg = styled.img`
  width: 100%;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
`;

const StyledTextArea = styled.textarea`
  border-bottom: 2px solid ${(props) => props.theme?.darkPurple} !important;
  min-height: 100px;
  max-height: 400px;
`;

const Error = styled.span`
  color: ${(props) => props.theme?.cardinal};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const StyledNote = styled.div`
  color: ${(props) => props.theme?.darkPurple};
  margin-top: -25px;
`;

const AuditTrailComment = (props) => {
  const {
    participantId,
    screenshot,
    onCancelClick,
    onSubmit,
    submissionError,
    setSubmissionError,
  } = props;

  const { t } = useTranslation();

  const [comments, setComments] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const commentTextAreaRef = useRef();

  const btnSaveErrorMngmt = {
    isSuccess: isSuccess,
    setIsSuccess: setIsSuccess,
    isError: isError,
    setIsError: setIsError,
  };

  const canDetectParticipant = useMemo(() => {
    return participantId && participantId.trim() !== "";
  }, [participantId]);

  const handleConfirmClick = () => {
    if (onSubmit) {
      onSubmit(comments);
    }
  };

  const handleComments = (e) => {
    setComments(e.target.value);
    if (e.target.value.length >= 10000) {
      setErrorMessage(`${t("appText.auditTrailComment.comment.large")}`);
    } else {
      setErrorMessage(null);
    }
  };

  useEffect(() => {
    if (submissionError) {
      setIsError(true);
      setSubmissionError(false);
    }
  }, [submissionError, setSubmissionError]);

  useEffect(() => {
    commentTextAreaRef.current?.focus();
  }, [commentTextAreaRef.current]);

  return (
    <div data-testid="audit-trail-comment">
      <StyledNote>
        <b>NOTE</b>: Comments will not be viewable by your study team
      </StyledNote>
      <div>
        {canDetectParticipant
          ? `${t("appText.auditTrailComment.participant")} ${participantId}`
          : `${t("appText.auditTrailComment.noParticipant")}.`}
      </div>
      <div>
        <StyledTextArea
          data-testid="audit-trail-comment-textarea"
          className={`form-control ${!errorMessage ? "mb-3" : ""}`}
          placeholder={`${t("appText.auditTrailComment.comments")}*`}
          value={comments}
          onChange={handleComments}
          rows="5"
          ref={commentTextAreaRef}
        ></StyledTextArea>
        {!!errorMessage && (
          <Error data-testid="error-message">{errorMessage}</Error>
        )}
      </div>
      <StyledScreenshotImg src={screenshot && screenshot.base64} />
      <ActionButtonsContainer>
        <SecondaryButton className="mr-2" onClick={onCancelClick}>
          {t("appText.auditTrailComment.cancel")}
        </SecondaryButton>
        <AnimatedButton
          text={t("appText.auditTrailComment.confirm")}
          icon={null}
          onClick={handleConfirmClick}
          disabled={comments.trim() === "" || !!errorMessage}
          {...btnSaveErrorMngmt}
        />
      </ActionButtonsContainer>
    </div>
  );
};

AuditTrailComment.defaultProps = {
  participantId: null,
  screenshot: null,
  onCancelClick: null,
  onSubmit: null,
  submissionError: false,
  setSubmissionError: null,
};

AuditTrailComment.propTypes = {
  participantId: PropTypes.string,
  screenshot: PropTypes.object,
  onCancelClick: PropTypes.func,
  onSubmit: PropTypes.func,
  submissionError: PropTypes.bool,
  setSubmissionError: PropTypes.func,
};

export default withTheme(AuditTrailComment);
